// packages/kern/src/scene.ts — `buildScene` (SPEC §3.9).
//
// KEIN DOM: außer `text-canvas.ts` darf keine Datei in `packages/kern/src`
// `document`, `window`, `navigator`, `fetch`, `localStorage` oder einen
// React-Import enthalten.
//
// Die Szene ist Geometrie OHNE Zielformat. Der Editor macht daraus React, der
// Export eine Zeichenkette; nur so ist das PDF deckungsgleich mit dem Schirm.
//
// PIXELGLEICHHEITSREGEL: derselbe `plan`, dasselbe Messer und dieselben `o`
// außer `o.editor` ergeben dieselben Knoten. `o.editor: false` lässt genau die
// Knoten mit `ed: true` weg — nichts anderes darf von `o.editor` abhängen.
// Der einzige zweite Schalter ist `o.hintergrund: false` (I8): er lässt genau
// Knoten 1 (das Papier-Rechteck) weg und sonst nichts.
//
// Rumpf aus `docs/quellen/understand/prototype.json`, reusable_snippets[12]
// (`render()`/`drawItem()`/`drawIcon()`), in der Reihenfolge und mit den
// Attributen aus der Tabelle in §3.9.

import { ferienSegs, type FerienQuelle } from "./ferien";
import { ICONS } from "./icons";
import {
  FER_H, FER_LABEL_W, ICON_H, LABEL_W, ROW_GAP, YEAR_H, kastenKanten,
  layout, miToX,
} from "./layout";
import { AUSWAHL, BAND, CHART_FAM, CMUTED, FERIEN_FILL, PAPER, PETROL, STYLES, isDark } from "./styles";
import { fitText, fontStr, SCHRIFT_MIN_PX, type Messer } from "./text";
import { TB } from "./texte";
import type { Ausrichtung, EigenesPiktogramm, Eintrag, Layout, PiktogrammKey, Plan, Spalte } from "./types";
import { maxMi, minMi } from "./zeit";

export type Knoten =
  | { k: "rect"; x: number; y: number; width: number; height: number;
      fill?: string; stroke?: string; strokeWidth?: number; strokeDasharray?: string;
      rx?: number; opacity?: number; titel?: string; ed?: true }
  | { k: "text"; x: number; y: number; text: string; fill: string;
      fontFamily: string; fontSize: number; fontWeight?: number; fontStyle?: "italic";
      textAnchor?: "middle" | "end"; dominantBaseline?: "central"; transform?: string; ed?: true }
  | { k: "path"; d: string; fill?: string; stroke?: string; strokeWidth?: number;
      strokeLinecap?: "round" | "square"; strokeLinejoin?: "round" | "bevel";
      fillRule?: "evenodd"; ed?: true }
  | { k: "line"; x1: number; y1: number; x2: number; y2: number;
      stroke: string; strokeWidth: number; opacity?: number; ed?: true }
  | { k: "g"; id?: string; transform?: string; kinder: Knoten[]; ed?: true }
  /** NUR fuer das Logo in der Exportkopfzeile (I6). Kommt im Blatt selbst nie vor. */
  | { k: "image"; x: number; y: number; width: number; height: number; href: string; ed?: true };

/** Trefferflaeche eines Balkens — Grundlage jeder Zeigerinteraktion. */
export interface Treffer { id: string; x0: number; x1: number; y0: number; y1: number; barY: number; barH: number }

export interface Marquee { x0: number; x1: number; y: number; h: number }

/** Die Exportkopfzeile. Beiwerk ueber dem Blatt, nie Teil des Blatts (I7, B10). */
export interface Kopfzeile {
  titel: string;
  /** Kunde/Stadt. Steht mit `stand` in einer Zeile: "Stadt Rheine · Stand 13". */
  kunde?: string;
  /** "Stand 13" — die Fassung des Plans. */
  stand: string;
  /** Deutsches Tagesdatum, z.B. "16.09.2026". */
  datum: string;
  /** data:-URI eines Logos (Chromium laedt ueber file:// und darf nichts nachladen). */
  logo?: string | null;
}

export interface SzeneOptionen {
  /** true = Hilfslinien, Auswahlrahmen, Griffe und Marquee werden gebaut (ed:true). */
  editor: boolean;
  auswahl?: string | null;
  marquee?: Marquee | null;
  ferien?: FerienQuelle | null;
  /** false = KEIN Papier-Rechteck als Knoten 1 (I8). Vorgabe true. */
  hintergrund?: boolean;
  /** Nur Beiwerk fuer den Export-Umschlag; aendert KEINE Geometrie des Blatts. */
  kopfzeile?: Kopfzeile | null;
  /** Nur Notiz in `szene.seite`; aendert KEINE Geometrie (T25). */
  seite?: { format: "a3" | "a4" | "frei" } | null;
}

export interface Szene {
  breite: number;
  hoehe: number;
  knoten: Knoten[];
  treffer: Treffer[];
  layout: Layout;
  /** Zahl der Eintraege, die komplett ausserhalb [start, end+1] liegen. */
  ausserhalb: number;
  kopfzeile: SzeneOptionen["kopfzeile"];
  seite: "a3" | "a4" | "frei";
}

/**
 * Ein Piktogramm als Gruppe, skaliert von 24er-Kästchen auf `size`.
 * `null` für `none` und für jeden unbekannten Namen.
 */
/**
 * Ein eigenes Symbol (T44), eingepasst in ein Quadrat der Kantenlänge `size` um
 * `(cx, cy)`. Die längere Seite der viewBox füllt das Quadrat, die kürzere steht mittig
 * — das Symbol wird nie verzerrt. Strichbreiten skalieren mit, wie bei den eingebauten.
 */
function eigenesKnoten(e: EigenesPiktogramm, cx: number, cy: number, size: number, farbe: string): Knoten | null {
  const [vx, vy, vb, vh] = e.viewBox;
  if (!(vb > 0 && vh > 0) || !e.pfade.length) return null;
  const k = size / Math.max(vb, vh);
  const ox = cx - (vb * k) / 2 - vx * k;
  const oy = cy - (vh * k) / 2 - vy * k;
  const kinder: Knoten[] = e.pfade.map((p): Knoten => {
    const pfad: Knoten = p.art === "s"
      ? { k: "path", d: p.d, fill: "none", stroke: farbe, strokeWidth: p.sw ?? 1,
          strokeLinecap: p.cap, strokeLinejoin: p.join }
      : { k: "path", d: p.d, fill: p.art === "p" ? PAPER : farbe, fillRule: p.fillRule };
    return p.transform ? { k: "g", transform: p.transform, kinder: [pfad] } : pfad;
  });
  return { k: "g", transform: "translate(" + ox + " " + oy + ") scale(" + k + ")", kinder };
}

function piktogrammKnoten(
  name: PiktogrammKey, cx: number, cy: number, size: number, farbe: string = PETROL,
  eigen?: EigenesPiktogramm,
): Knoten | null {
  if (name === "eigen") return eigen ? eigenesKnoten(eigen, cx, cy, size, farbe) : null;
  const spec = ICONS[name];
  if (!spec) return null;
  const k = size / 24;
  const kinder: Knoten[] = [];
  // Eine Sicht mit immer drei Stellen: `["f", d]` ist auf
  // `[string, string, number?]` zuweisbar, weil die dritte optional ist. So
  // braucht der Zugriff auf die Strichbreite keine Verengung über `p[0]`.
  const pfade: ReadonlyArray<readonly [string, string, number?]> = spec;
  for (const p of pfade) {
    if (p[0] === "f") kinder.push({ k: "path", d: p[1], fill: farbe });
    else if (p[0] === "p") kinder.push({ k: "path", d: p[1], fill: PAPER });
    else kinder.push({ k: "path", d: p[1], fill: "none", stroke: farbe, strokeWidth: p[2] ?? 2, strokeLinecap: "round", strokeLinejoin: "round" });
  }
  return { k: "g", transform: "translate(" + (cx - size / 2) + " " + (cy - size / 2) + ") scale(" + k + ")", kinder };
}

/** `"start"` ist der Standardwert und wird NICHT geschrieben (§3.9 Schritt 12). */
function ankerVon(align: Ausrichtung): "middle" | "end" | undefined {
  return align === "left" ? undefined : align === "right" ? "end" : "middle";
}

export function buildScene(plan: Plan, m: Messer, o: SzeneOptionen): Szene {
  const L = layout(plan);
  const fs = plan.fontScale;
  const knoten: Knoten[] = [];
  const treffer: Treffer[] = [];
  let ausserhalb = 0;
  const lo = minMi(plan), hi = maxMi(plan);

  // 1 — Grund. `hintergrund: false` lässt genau diesen einen Knoten weg (I8).
  if (o.hintergrund !== false) {
    knoten.push({ k: "rect", x: 0, y: 0, width: L.W, height: L.height, fill: PAPER });
  }

  // 2 — Quartalsband. Gehört zum Druckbild, ist NIE `ed`.
  L.cols.forEach((c) => {
    if (c.kind === "q") {
      knoten.push({ k: "rect", x: c.x, y: L.monthY - 4, width: c.w, height: L.bodyBottom - L.monthY + 8, fill: BAND, opacity: 0.75 });
    }
  });

  // 3 — Hilfslinien. Im Editor immer, wenn `guides` an ist. Im Export nur,
  // wenn zusätzlich `guidesExport` an ist (T41) — dann sind sie Druckbild und
  // tragen KEIN `ed`, im Editor also genau so, wie sie in die Datei gehen.
  // Die Regel „ein Export trägt nie `ed`-Knoten" (§3.9) bleibt damit
  // wörtlich wahr.
  const hilfsDruck = !!plan.guidesExport;
  if (plan.guides && (o.editor || hilfsDruck)) {
    const kinder: Knoten[] = [];
    L.cols.forEach((c, i) => {
      if (i) kinder.push({ k: "line", x1: c.x, y1: L.bodyY - 6, x2: c.x, y2: L.bodyBottom + 2, stroke: PETROL, strokeWidth: 0.5, opacity: 0.13 });
    });
    L.spuren.forEach((spur) => spur.reihen.forEach((r) => {
      kinder.push({ k: "line", x1: L.x0, y1: r.y + r.h - ROW_GAP / 2, x2: L.x1, y2: r.y + r.h - ROW_GAP / 2, stroke: PETROL, strokeWidth: 0.5, opacity: 0.08 });
    }));
    // `ed` nur, solange sie NICHT gedruckt werden — sonst wirft der Export
    // sie wieder hinaus.
    knoten.push(hilfsDruck ? { k: "g", kinder } : { k: "g", kinder, ed: true });
  }

  // 4 — Titel.
  if (plan.title) {
    knoten.push({ k: "text", x: L.pad, y: L.padTop + 17, text: plan.title, fill: PETROL, fontFamily: CHART_FAM, fontSize: 18 * fs, fontWeight: 500 });
  }

  // 5 — Jahresbänder. Gruppen unter 26 px Breite fallen weg.
  const gruppen: Array<{ year: number; cols: Spalte[] }> = [];
  L.cols.forEach((c) => {
    const last = gruppen[gruppen.length - 1];
    if (last && last.year === c.year) last.cols.push(c);
    else gruppen.push({ year: c.year, cols: [c] });
  });
  gruppen.forEach((g) => {
    const erste = g.cols[0]!, letzte = g.cols[g.cols.length - 1]!;
    const a = erste.x, b = letzte.x + letzte.w;
    const span = b - a, w = Math.min(span - 6, 118), cx = (a + b) / 2;
    if (w < 26) return;
    knoten.push({ k: "rect", x: cx - w / 2, y: L.yearY, width: w, height: YEAR_H, fill: BAND, rx: 2 });
    knoten.push({ k: "text", x: cx, y: L.yearY + YEAR_H / 2 + 4.5, text: String(g.year), textAnchor: "middle", fill: CMUTED, fontFamily: CHART_FAM, fontSize: 12.5 * fs });
  });

  // 6 — Monatsköpfe.
  L.cols.forEach((c) => {
    const roh = Math.min(19, c.w / (c.label.length * 0.48)) * fs;
    knoten.push({ k: "text", x: c.x + c.w / 2, y: L.monthY + 21, text: c.label, textAnchor: "middle", fill: CMUTED, fontFamily: CHART_FAM, fontSize: Math.max(9, Math.min(19 * fs, roh)) });
  });

  // 7 — Spurenrahmen und gedrehte Beschriftung.
  L.spuren.forEach((spur) => {
    knoten.push({ k: "rect", x: L.pad, y: spur.y, width: LABEL_W, height: spur.h - ROW_GAP, rx: 6, fill: "none", stroke: PETROL, strokeWidth: 1 });
    const cy = spur.y + (spur.h - ROW_GAP) / 2, cx = L.pad + LABEL_W / 2;
    knoten.push({
      k: "text", x: cx, y: cy, text: spur.name, textAnchor: "middle", fill: PETROL,
      fontFamily: CHART_FAM, fontSize: 12.5 * fs,
      transform: "rotate(-90 " + cx + " " + cy + ")", dominantBaseline: "central",
    });
  });

  // 8 — Balken, in `plan.items`-Reihenfolge.
  plan.items.forEach((it) => {
    const gebaut = eintragKnoten(plan, L, m, it, lo, hi);
    if (gebaut === "ausserhalb") { ausserhalb++; return; }
    if (!gebaut) return;
    knoten.push(gebaut.gruppe);
    treffer.push(gebaut.treffer);
  });

  // 9 — Ferienzeile.
  if (plan.ferien) {
    const y = L.ferY;
    knoten.push({ k: "rect", x: L.pad, y, width: L.x1 - L.pad, height: FER_H, fill: "none", stroke: PETROL, strokeWidth: 0.9 });
    ferienSegs(plan, o.ferien ?? null).forEach((s) => {
      const a = miToX(L, s.a), b = miToX(L, s.b);
      if (b - a < 0.6) return;
      knoten.push({ k: "rect", x: a, y: y + 0.5, width: b - a, height: FER_H - 1, fill: FERIEN_FILL, titel: s.names.join(" / ") });
      if (plan.ferienNames && b - a > 52) {
        knoten.push({ k: "text", x: (a + b) / 2, y: y + FER_H / 2 + 3.5, text: s.names[0] ?? TB.ferienFallback, textAnchor: "middle", fill: PETROL, fontFamily: CHART_FAM, fontSize: 9 * fs });
      }
    });
    // ZULETZT die Plakette: sie verdeckt die Segmente von x=22 bis x=134 — so
    // ist das Druckbild heute, und so bleibt es (§3.9).
    knoten.push({ k: "rect", x: L.pad, y, width: FER_LABEL_W, height: FER_H, fill: FERIEN_FILL });
    knoten.push({ k: "text", x: L.pad + FER_LABEL_W / 2, y: y + FER_H / 2 + 4, text: TB.ferienLabel, textAnchor: "middle", fill: PETROL, fontFamily: CHART_FAM, fontSize: 11 * fs });
  }

  // 10 — Auswahlrahmen und die zwei Griffe, nur im Editor.
  if (o.editor && o.auswahl) {
    const h = treffer.find((t) => t.id === o.auswahl);
    if (h) {
      const kinder: Knoten[] = [
        { k: "rect", x: h.x0 - 2.5, y: h.y0 - 2.5, width: h.x1 - h.x0 + 5, height: h.y1 - h.y0 + 5, rx: 9, fill: "none", stroke: AUSWAHL, strokeWidth: 1.5, strokeDasharray: "4 3" },
      ];
      [h.x0, h.x1].forEach((X) => {
        kinder.push({ k: "rect", x: X - 3.5, y: (h.y0 + h.y1) / 2 - 8, width: 7, height: 16, rx: 3, fill: AUSWAHL });
      });
      // Unterkante: zieht den Kasten über weitere halbe Zeilen (T42).
      kinder.push({ k: "rect", x: (h.x0 + h.x1) / 2 - 8, y: h.y1 - 3.5, width: 16, height: 7, rx: 3, fill: AUSWAHL });
      knoten.push({ k: "g", kinder, ed: true });
    }
  }

  // 11 — Marquee, nur im Editor. Für Touch kommen KEINE weiteren Knoten in die
  // Szene (B4): die 44-px-Zone rechnet `usePointerInteraktion` aus `treffer`.
  if (o.editor && o.marquee) {
    const q = o.marquee;
    knoten.push({
      k: "rect", x: Math.min(q.x0, q.x1), y: q.y, width: Math.abs(q.x1 - q.x0), height: q.h, rx: 8,
      fill: "rgba(10,122,136,.12)", stroke: AUSWAHL, strokeWidth: 1.2, strokeDasharray: "4 3", ed: true,
    });
  }

  return {
    breite: L.W, hoehe: L.height, knoten, treffer, layout: L, ausserhalb,
    kopfzeile: o.kopfzeile ?? null,
    seite: o.seite ? o.seite.format : "frei",
  };
}

/** Größter Abstand Kastenoberkante -> erste Textzeile, wenn ein Piktogramm
 *  über dem Kasten steht (T45). Bei üblichen Zeilen bis ~56 px greift er nicht. */
export const TEXT_ABSTAND_MAX = 22;

/**
 * Ein Balken, wortgleich zu `drawItem()` des Prototyps.
 *
 * `null` = die Spur oder die Zeile fehlt (nichts zeichnen, KEIN Treffer).
 * `"ausserhalb"` = der Eintrag liegt komplett außerhalb des Zeitraums; er
 * wird nicht gezeichnet, landet nicht in `treffer`, bleibt aber in den Daten
 * und wird gezählt (T34: nie still verwerfen).
 */
function eintragKnoten(
  plan: Plan, L: Layout, m: Messer, it: Eintrag, lo: number, hi: number,
): { gruppe: Knoten; treffer: Treffer } | null | "ausserhalb" {
  const spur = L.nachId[it.lane];
  if (!spur) return null;
  const reihe = spur.reihen[Math.min(Math.floor(it.row), spur.reihen.length - 1)];
  if (!reihe) return null;
  const st = STYLES[it.style] ?? STYLES.solid;
  if (it.b <= lo || it.a >= hi) return "ausserhalb";

  let x1 = miToX(L, Math.max(it.a, it.b));
  let x0 = miToX(L, Math.min(it.a, it.b));
  if (x1 - x0 < 8) x1 = x0 + 8;
  // Ober- und Unterkante aus dem Halbzeilenraster (T42). Für einen Kasten
  // über genau eine ganze Zeile ist das `reihe.y + reihe.iconH` bis
  // `+ L.rowH` — wie bisher.
  const kante = kastenKanten(L, spur, it.row, it.hoehe);
  const barY = kante.y0, barH = kante.y1 - kante.y0;
  const ganzeZeile = Number.isInteger(it.row);
  const base = (it.size ? it.size : 11) * plan.fontScale;
  // T45 — ein Meilenstein ist ein Zeitpunkt, kein Zeitraum. Steht sein Text
  // auch getrennt und in Mindestschrift über den Kasten hinaus, wird der
  // KASTEN breiter, um dieselbe Mitte, bis das längste Wort hineinpasst.
  // Passt der Text, bleibt der Kasten, wie er war (die Goldens verlangen es).
  if (it.style === "meilen" && it.drehung !== 90) {
    const innen = Math.max(12, x1 - x0 - 18);
    const probe = fitText(it.text, innen, barH - 6, base, st.weight, st.italic, m);
    const fp = fontStr(probe.size, st.weight, st.italic);
    const breiteste = probe.lines.reduce((mx, l) => Math.max(mx, m.breite(l, fp)), 0);
    if (probe.lines.length * probe.lh > barH - 6 || breiteste > innen + 0.5) {
      const fm = fontStr(SCHRIFT_MIN_PX, st.weight, st.italic);
      const wort = String(it.text ?? "").split(/\s+/).reduce((mx, w) => Math.max(mx, m.breite(w, fm)), 0);
      const noetig = Math.ceil(wort) + 18;
      if (noetig > x1 - x0) {
        const mitte = (x0 + x1) / 2;
        x0 = mitte - noetig / 2;
        x1 = mitte + noetig / 2;
      }
    }
  }
  const fill = it.color || st.fill;
  const ink = it.color ? (isDark(it.color) ? "#FFFFFF" : PETROL) : st.ink;

  const kinder: Knoten[] = [];
  if (st.fill !== "none" || st.stroke) {
    kinder.push({
      k: "rect", x: x0, y: barY, width: x1 - x0, height: barH, rx: 8,
      fill: st.fill === "none" ? "none" : fill,
      stroke: st.stroke ?? "none", strokeWidth: st.sw ?? 1, strokeDasharray: st.dash,
    });
  }
  const padX = st.fill === "none" && !st.stroke ? 4 : 9;
  // Gedrehter Text (T42): Länge und Tiefe tauschen die Rollen — die Zeile
  // läuft den Kasten hinauf, weitere Zeilen stapeln sich nach rechts. Gesetzt
  // wird in einem gedachten waagerechten Kasten um dieselbe Mitte, danach
  // dreht `rotate(-90 …)` jede Zeile um diese Mitte. Das ist dieselbe Drehung
  // wie bei den Spurbeschriftungen; gelesen wird von unten nach oben.
  const senkrecht = it.drehung === 90;
  const mitIcon = !!it.icon && it.icon !== "none" && (it.icon !== "eigen" || !!it.eigenesIcon);
  const iconFarbe = /^#[0-9a-f]{6}$/i.test(it.iconColor ?? "") ? it.iconColor : PETROL;
  // T43 — das Piktogramm gehört zum Text. Zwei Lagen:
  //  * Kasten ab einer GANZEN Zeile: das Piktogramm bleibt in der Zone über
  //    dem Kasten (das Druckbild der Vorlagen), aber der Text rückt nach OBEN,
  //    direkt unter das Symbol. Vorher stand er in der Kastenmitte — auf einem
  //    A4-Blatt mit verteilten Zeilen gut drei Zentimeter vom Symbol entfernt.
  //  * Kasten ab einer HALBEN Zeile: darüber liegt keine eigene Zone (dort ist
  //    die obere Hälfte, womöglich ein anderer Kasten). Das Piktogramm steht
  //    dann IM Kasten links vor dem Text, so hoch, wie der Kasten erlaubt.
  const inline = mitIcon && !ganzeZeile && !senkrecht;
  const iconInline = inline ? Math.max(8, Math.min(22, barH - 4)) : 0;
  const breite = x1 - x0;
  const laenge = senkrecht ? barH : breite;
  const tiefe = senkrecht ? breite : barH;
  // Mitte des Textfeldes. Ohne Inline-Symbol ist das wörtlich die Kastenmitte
  // `(x0 + x1) / 2` wie bisher — die Goldens verlangen es bitgenau.
  const cx = inline ? (x0 + iconInline + 4 + x1) / 2 : (x0 + x1) / 2;
  const cy = barY + barH / 2;
  const feld = senkrecht ? laenge : (inline ? x1 - (x0 + iconInline + 4) : laenge);
  const vx0 = cx - feld / 2, vx1 = cx + feld / 2;
  const maxW = Math.max(12, feld - padX * 2);
  const satz = fitText(it.text, maxW, tiefe - 6, base, st.weight, st.italic, m);
  const anchor = ankerVon(it.align);
  const tx = it.align === "left" ? vx0 + padX : it.align === "right" ? vx1 - padX : cx;
  // Mit Piktogramm über dem Kasten steht der Text in der Kastenmitte wie seine
  // Nachbarn — aber in hohen Kästen höchstens TEXT_ABSTAND_MAX unter der
  // Oberkante, damit er beim Symbol bleibt (T45 ersetzt das starre „oben“
  // aus T43, das in normalen Zeilen schief neben den Nachbarn stand).
  const mittig = cy - (satz.lines.length - 1) * satz.lh / 2;
  const obenBuendig = mitIcon && ganzeZeile && !senkrecht;
  const ersteMitte = obenBuendig
    ? Math.min(mittig, barY + TEXT_ABSTAND_MAX + satz.lh / 2)
    : mittig;
  const startY = ersteMitte + satz.size * 0.34;

  // Piktogramm VOR den Textzeilen einhängen — die Knotenfolge ist Druckbild.
  if (mitIcon) {
    let ix: number;
    let iy: number;
    let groesse = 26;
    if (inline) {
      groesse = iconInline;
      ix = x0 + padX / 2 + groesse / 2;
      iy = ersteMitte;
    } else if (senkrecht && !ganzeZeile) {
      // Selten: gedreht UND ab halber Zeile. Keine Zone darüber, also oben in
      // den Kasten. Der gedrehte Text rückt dafür nicht zur Seite — bewusster
      // Kompromiss, weil es den Fall in keinem Angebot gibt.
      groesse = Math.max(8, Math.min(22, breite - 4));
      ix = (x0 + x1) / 2;
      iy = barY + 2 + groesse / 2;
    } else if (senkrecht) {
      ix = it.iconPos === "left" ? x0 + 20 : it.iconPos === "right" ? x1 - 20 : (x0 + x1) / 2;
      iy = reihe.y + ICON_H / 2;
    } else {
      // Am TEXTBLOCK ausgerichtet, nicht am Kasten: bei linksbündigem Text
      // steht das Symbol über dem Textanfang, nicht in der Kastenmitte.
      const f = fontStr(satz.size, st.weight, st.italic);
      const w = satz.lines.reduce((mx, l) => Math.max(mx, m.breite(l, f)), 0);
      const links = anchor === undefined ? tx : anchor === "end" ? tx - w : tx - w / 2;
      const rechts = links + w;
      ix = it.iconPos === "left" ? links + 13
        : it.iconPos === "right" ? rechts - 13
        : anchor === "middle" ? tx : (links + rechts) / 2;
      if (breite >= 26) ix = Math.max(x0 + 13, Math.min(x1 - 13, ix));
      iy = reihe.y + ICON_H / 2;
    }
    const pikto = piktogrammKnoten(it.icon, ix, iy, groesse, iconFarbe, it.eigenesIcon);
    if (pikto) kinder.push(pikto);
  }
  const drehen = senkrecht ? "rotate(-90 " + cx + " " + cy + ")" : undefined;
  satz.lines.forEach((ln, i) => {
    kinder.push({
      k: "text", x: tx, y: startY + i * satz.lh, text: ln, textAnchor: anchor, fill: ink,
      fontFamily: CHART_FAM, fontSize: satz.size, fontWeight: st.weight || 400,
      fontStyle: st.italic ? "italic" : undefined, transform: drehen,
    });
  });

  // Frei platziert (T43): der GANZE Eintrag wandert um dx/dy, ohne Raster.
  // Nur dann steht ein `transform` an der Gruppe — sonst bleibt der Knoten,
  // wie er war, und die Goldens sehen keinen Unterschied.
  const dx = it.frei && Number.isFinite(it.dx) ? it.dx : 0;
  const dy = it.frei && Number.isFinite(it.dy) ? it.dy : 0;
  const gruppe: Knoten = dx || dy
    ? { k: "g", id: it.id, transform: "translate(" + dx + " " + dy + ")", kinder }
    : { k: "g", id: it.id, kinder };
  const y0 = (ganzeZeile && !inline) ? reihe.y : barY;
  return {
    gruppe,
    // `y0` ist `reihe.y`, NICHT `barY`: die Piktogrammzone gehört zur
    // Trefferfläche, ein Klick auf das Piktogramm wählt den Balken (§3.9).
    // Beginnt der Kasten auf einer HALBEN Zeile, gehört ihm die Zone darüber
    // nicht — dort liegt die obere Hälfte, womöglich ein anderer Kasten (T42).
    treffer: { id: it.id, x0: x0 + dx, x1: x1 + dx, y0: y0 + dy, y1: barY + barH + dy, barY: barY + dy, barH },
  };
}
